// Presentación de «Mi tienda» (mock `portal-vendedor-tienda.html`). Funciones puras: la pantalla
// solo compone, y así el orden de las listas y los rótulos por rango se prueban sin render.
import { formatShortDateMx, formatTimeMx } from '@/lib/dateHelpers';
import { plural } from '@/shared/lib/format/plural';
import type {
    StoreProspectsBySellerRow,
    StoreRankingRow,
    StoreSaleRow,
    StoreSummaryRange,
    StoreTopSkuRow,
} from '@/api/services/retail-wizard/contract';

/** Los tres chips de la barra de rango, en el orden del mock. */
export const STORE_RANGES: { value: StoreSummaryRange; label: string }[] = [
    { value: 'today', label: 'Hoy' },
    { value: 'week', label: 'Esta semana' },
    { value: 'month', label: 'Este mes' },
];

const RANGE_NOUN: Record<StoreSummaryRange, string> = {
    today: 'del día',
    week: 'de la semana',
    month: 'del mes',
};

const RANGE_KPI: Record<StoreSummaryRange, string> = {
    today: 'Ventas hoy',
    week: 'Ventas semana',
    month: 'Ventas mes',
};

const RANGE_WHEN: Record<StoreSummaryRange, string> = {
    today: 'hoy',
    week: 'esta semana',
    month: 'este mes',
};

/** «del día» / «de la semana» / «del mes» — cierra los títulos de las cards. */
export function rangeNoun(range: StoreSummaryRange): string {
    return RANGE_NOUN[range];
}

/**
 * Rótulo del KPI de ventas. Sigue al rango en vez de decir siempre «Ventas hoy» como el mock: con
 * el chip «Este mes» puesto, ese rótulo nombra una ventana que no es la que se está contando.
 */
export function rangeKpiLabel(range: StoreSummaryRange): string {
    return RANGE_KPI[range];
}

/** «hoy» / «esta semana» / «este mes» — meta de la cabecera de la card de meta. */
export function rangeWhen(range: StoreSummaryRange): string {
    return RANGE_WHEN[range];
}

/** Etiquetas cortas de la financiera, tal cual el `chipFin` del mock. */
const FINANCIER_CHIP: Record<
    string,
    { label: string; variant: 'success' | 'info' | 'warning' | 'secondary' }
> = {
    SUREKEEP: { label: 'SK', variant: 'success' },
    PAYJOY: { label: 'PJ', variant: 'info' },
    CREDIYA: { label: 'CY', variant: 'warning' },
    LESPAGO: { label: 'LP', variant: 'secondary' },
    INNOVA: { label: 'INV', variant: 'secondary' },
};

const CASH_CHIP = { label: 'CTD', variant: 'secondary' } as const;

/**
 * Otras grafías de la misma financiera. El catálogo de productos escribe «KREDIYA» y códigos cortos
 * donde las ventas escriben el nombre canónico.
 */
const FINANCIER_ALIAS: Record<string, string> = {
    KREDIYA: 'CREDIYA',
    SK: 'SUREKEEP',
    PJ: 'PAYJOY',
    KY: 'CREDIYA',
};

/** El código canónico de una financiera; `null` cuando el código no es de ninguna que conozcamos. */
export function financierKeyFromCode(code: string): string | null {
    const upper = code.trim().toUpperCase();
    if (upper in FINANCIER_CHIP) return upper;
    return FINANCIER_ALIAS[upper] ?? null;
}

/**
 * Chip de la financiera de una venta. SureKeep NO llega como `externalProvider`: su crédito propio
 * se persiste `type: 'CREDIT'` con proveedor nulo, así que se deriva del tipo.
 */
export function financierChipOf(
    type: string,
    externalProvider: string | null,
): { label: string; variant: 'success' | 'info' | 'warning' | 'secondary' } {
    if (externalProvider) {
        const key = financierKeyFromCode(externalProvider);
        return key === null
            ? { label: externalProvider, variant: 'secondary' }
            : FINANCIER_CHIP[key];
    }
    return type === 'CREDIT' ? FINANCIER_CHIP.SUREKEEP : CASH_CHIP;
}

/** Nombre del vendedor; sin nombre, el id acortado — el ULID crudo se come la columna. */
export function sellerLabel(sellerName: string | null, sellerRetailUserId: string): string {
    return sellerName ?? `Vendedor …${sellerRetailUserId.slice(-6)}`;
}

/**
 * Columna angosta de vendedor de las filas densas. El mock pinta un código corto («V-01») que no
 * existe en nuestro modelo, y el id es un ULID que DEC-012 prohíbe mostrar: van las iniciales, con
 * el nombre completo en el `title` de la celda.
 */
export function sellerInitials(sellerName: string | null): string {
    if (!sellerName) return '—';
    const initials = sellerName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join('');
    return initials || '—';
}

/**
 * Primera columna de la fila de venta: la hora sola alcanza dentro del día; en semana y mes hace
 * falta el día, o dos ventas de fechas distintas se leen como si fueran de la misma tarde.
 */
export function saleWhenLabel(soldAt: string, range: StoreSummaryRange): string {
    return range === 'today' ? formatTimeMx(soldAt) : formatShortDateMx(soldAt);
}

/**
 * Porcentaje entero de avance contra la meta. `null` cuando no hay meta con la que comparar — el
 * silo la manda en `null` cuando no la pudo leer o cuando la ventana no tiene meta.
 */
export function goalPercent(amountCents: number, goalCents: number | null): number | null {
    if (goalCents === null || goalCents <= 0) return null;
    return Math.round((amountCents / goalCents) * 100);
}

/**
 * Por qué no hay meta que pintar, en la frase larga del cuerpo de la card. La meta en `null` llega
 * en un 200: no está configurada, la lectura no falló. Mismo texto que el Home para el mismo caso.
 */
export function goalMissingText(range: StoreSummaryRange): string {
    return `Sin meta configurada para ${rangeWhen(range)}. Pídesela a tu gerente.`;
}

/** Porcentaje de ventas con crédito propio de SureKeep. Sin ventas, 0 (no hay mix que medir). */
export function mixPercent(surekeepCount: number, salesCount: number): number {
    if (salesCount <= 0) return 0;
    return Math.round((surekeepCount / salesCount) * 100);
}

/**
 * Ranking por monto vendido, de mayor a menor: los sembrados en cero caen al final. Empata por
 * nombre —los sin nombre al último— y después por id, el mismo orden total que arma el silo, para
 * que dos lecturas seguidas no muestren a los ceros en distinto lugar.
 */
export function sortedRanking(ranking: StoreRankingRow[]): StoreRankingRow[] {
    return [...ranking].sort(
        (a, b) =>
            b.amountCents - a.amountCents ||
            compareSellerName(a.sellerName, b.sellerName) ||
            a.sellerRetailUserId.localeCompare(b.sellerRetailUserId),
    );
}

/** Alfabético con los sin nombre al final, para que el desempate no dependa de un `null`. */
function compareSellerName(a: string | null, b: string | null): number {
    if (a === b) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return a.localeCompare(b, 'es-MX');
}

/**
 * Las últimas ventas de la tienda, de la más reciente a la más vieja. El silo ya manda solo las 20
 * de la cola; ordenar aquí es para no depender de en qué sentido las devuelva el índice.
 */
export function sortedStoreSales(sales: StoreSaleRow[]): StoreSaleRow[] {
    return [...sales].sort((a, b) => b.soldAt.localeCompare(a.soldAt));
}

/** Renglón del top con SKU; el que llegara sin equipo se descarta. */
export type RankedTopSku = StoreTopSkuRow & { sku: string };

/**
 * SKUs top por unidades vendidas, como el `renderSKUs` del mock; a igual conteo, el de más monto y
 * después alfabético. La lista pinta «#1..#N», así que el orden no puede depender del índice.
 */
export function sortedTopSkus(rows: StoreTopSkuRow[]): RankedTopSku[] {
    return rows
        .filter((row): row is RankedTopSku => row.sku !== null)
        .sort(
            (a, b) =>
                b.count - a.count ||
                b.amountCents - a.amountCents ||
                a.sku.localeCompare(b.sku, 'es-MX'),
        );
}

/**
 * Rótulo de la lista de ventas: «N últimas», que avisa que es una cola y no el total del rango.
 * Cuando la ventana tiene más de las que caben, se dice cuántas de cuántas — «20 últimas» a secas
 * hace creer que la tienda vendió veinte veces en el mes.
 */
export function recentSalesLabel(shown: number, salesCount: number): string {
    return salesCount > shown
        ? `últimas ${String(shown)} de ${String(salesCount)}`
        : plural(shown, 'última', 'últimas');
}

/** Aviso de ventana recortada, con el titular y el cuerpo ya resueltos. */
export interface TruncationNotice {
    title: string;
    body: string;
}

/**
 * Qué avisar cuando un barrido topó. Nombra SOLO lo que se recortó: los dos barridos del silo topan
 * por separado, y un aviso que los mezcla afirma de más sobre el que sí leyó todo.
 */
export function truncationNotice(
    truncatedSales: boolean,
    truncatedProspects: boolean,
): TruncationNotice | null {
    if (truncatedSales && truncatedProspects) {
        return {
            title: 'Este periodo superó el tope de lectura.',
            body: 'Los totales de venta, el ranking y los SKUs top son un mínimo, la lista de ventas no es la cola completa del periodo, y los prospectos abiertos también son un mínimo. Elige un rango más corto para ver las ventas exactas.',
        };
    }
    if (truncatedSales) {
        return {
            title: 'Este periodo superó el tope de lectura.',
            body: 'Los totales de venta, el ranking y los SKUs top son un mínimo, no el total, y la lista de ventas no es la cola completa del periodo. Elige un rango más corto para ver los números exactos. Los prospectos abiertos sí están completos.',
        };
    }
    if (truncatedProspects) {
        return {
            title: 'El conteo de prospectos superó el tope de lectura.',
            body: 'Los prospectos abiertos de la tienda son un mínimo, no el total. Las ventas de este periodo sí están completas, y los prospectos no dependen del rango: cambiarlo no los completa.',
        };
    }
    return null;
}

/** Prospectos abiertos de la cabecera. Con el barrido recortado, el total es un piso. */
export function prospectsTotalLabel(openTotal: number, truncatedProspects: boolean): string {
    const total = plural(openTotal, 'abierto', 'abiertos');
    return truncatedProspects ? `al menos ${total}` : total;
}

/**
 * Cuántos vendedores hay en el ranking. Sin padrón la lista trae solo a quien vendió, así que el
 * conteo no puede leerse como el tamaño de la tienda.
 */
export function rankingMetaLabel(shown: number, sellersAssigned: number | null): string {
    const sellers = plural(shown, 'vendedor', 'vendedores');
    return sellersAssigned === null ? `${sellers} con ventas` : sellers;
}

/**
 * Prospectos por vendedor: primero el que más tiene abiertos, que es a quien el encargado va a
 * empujar; a igual carga, alfabético.
 */
export function sortedProspectsBySeller(
    rows: StoreProspectsBySellerRow[],
): StoreProspectsBySellerRow[] {
    return [...rows].sort(
        (a, b) =>
            b.openCount - a.openCount ||
            sellerLabel(a.sellerName, a.sellerRetailUserId).localeCompare(
                sellerLabel(b.sellerName, b.sellerRetailUserId),
                'es-MX',
            ),
    );
}
